import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Overline, SectionCard, Disclaimer } from "../components/shared";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { Loader2, Save, Cpu, SlidersHorizontal, RotateCcw } from "lucide-react";

const MODEL_GROUPS = [
  {
    provider_label: "OpenAI / ChatGPT",
    models: [
      { label: "GPT‑5.6", provider: "openai", model: "gpt-5.6-sol", note: "OpenAI's newest flagship" },
      { label: "GPT‑5.4", provider: "openai", model: "gpt-5.4", note: "Strong all-round" },
      { label: "GPT‑5.4 Mini", provider: "openai", model: "gpt-5.4-mini", note: "Fast & cost-efficient" },
    ],
  },
  {
    provider_label: "Anthropic",
    models: [
      { label: "Claude Sonnet 4.6", provider: "anthropic", model: "claude-sonnet-4-6", note: "Deep analytical reasoning" },
    ],
  },
  {
    provider_label: "Google",
    models: [
      { label: "Gemini 3.1 Pro", provider: "gemini", model: "gemini-3.1-pro-preview", note: "Large context for whitepapers" },
    ],
  },
];

export default function Settings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)); }, []);
  if (!settings) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const selectModel = (m) => setSettings({ ...settings, llm_provider: m.provider, llm_model: m.model });
  const setWeight = (name, v) => setSettings({ ...settings, scoring_weights: { ...settings.scoring_weights, [name]: Number(v) } });
  const resetWeights = () => {
    const keys = Object.keys(settings.scoring_weights);
    const eq = Number((1 / keys.length).toFixed(4));
    setSettings({ ...settings, scoring_weights: Object.fromEntries(keys.map((k) => [k, eq])) });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { llm_provider: settings.llm_provider, llm_model: settings.llm_model, scoring_weights: settings.scoring_weights, research_frequency: settings.research_frequency });
      toast.success("Settings saved");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const totalWeight = Object.values(settings.scoring_weights).reduce((a, b) => a + Number(b), 0);

  return (
    <div className="space-y-5 fade-up max-w-6xl" data-testid="settings-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div><Overline>Admin</Overline><h1 className="font-heading font-extrabold text-3xl tracking-tight mt-1">Settings</h1></div>
        <button onClick={save} disabled={saving} data-testid="settings-save" className="inline-flex items-center gap-2 h-9 px-4 bg-primary text-primary-foreground text-sm font-semibold rounded-sm hover:bg-primary/90 disabled:opacity-60 transition-colors">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Changes
        </button>
      </div>

      <SectionCard title="AI Research Model" testId="settings-model">
        <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground"><Cpu className="w-4 h-4" /> Powers the AI analyst, project research, and Design My ICO.</div>
        <div className="space-y-4">
          {MODEL_GROUPS.map((g) => (
            <div key={g.provider_label}>
              <div className="overline text-muted-foreground mb-2">{g.provider_label}</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {g.models.map((m) => {
                  const active = settings.llm_model === m.model;
                  return (
                    <button key={m.model} onClick={() => selectModel(m)} data-testid={`model-${m.model}`}
                      className={cn("text-left border p-4 transition-colors relative", active ? "border-primary ring-1 ring-primary bg-accent" : "border-border/60 hover:border-primary/40")}>
                      {active && <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-semibold text-primary"><span className="w-1.5 h-1.5 rounded-full bg-primary" /> Active</span>}
                      <div className="font-heading font-bold text-sm">{m.label}</div>
                      <div className="text-xs text-muted-foreground mt-1">{m.note}</div>
                      <div className="num text-[11px] text-muted-foreground mt-2">{m.provider} · {m.model}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <Disclaimer className="mt-3">All models run on the Emergent universal key — no separate API key needed. Switch anytime; changes apply to new AI requests.</Disclaimer>
      </SectionCard>

      <SectionCard title="Research Frequency" testId="settings-frequency">
        <select value={settings.research_frequency} onChange={(e) => setSettings({ ...settings, research_frequency: e.target.value })} className="h-9 px-2.5 text-sm border border-border rounded-sm bg-card">
          {["Hourly", "Daily", "Weekly"].map((f) => <option key={f}>{f}</option>)}
        </select>
        <span className="text-xs text-muted-foreground ml-3">Full market scan cadence. Critical fields refresh more often as TGE approaches.</span>
      </SectionCard>

      <SectionCard title="Scoring Weights" testId="settings-weights"
        right={<button onClick={resetWeights} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"><RotateCcw className="w-3.5 h-3.5" /> Reset to equal</button>}>
        <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground"><SlidersHorizontal className="w-4 h-4" /> Adjust how each component contributes to the ICO Intelligence Score. Current total: <span className={cn("num font-semibold ml-1", Math.abs(totalWeight - 1) < 0.02 ? "text-emerald-600" : "text-amber-600")}>{totalWeight.toFixed(2)}</span></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {Object.entries(settings.scoring_weights).map(([name, w]) => (
            <div key={name} className="flex items-center gap-3">
              <span className="text-sm flex-1 truncate">{name}</span>
              <input type="range" min="0" max="0.2" step="0.005" value={w} onChange={(e) => setWeight(name, e.target.value)} className="w-28 accent-[hsl(222_47%_11%)]" data-testid={`weight-${name.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
              <span className="num text-xs w-10 text-right text-muted-foreground">{Number(w).toFixed(3)}</span>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard title="Account" testId="settings-account">
        <div className="text-sm"><span className="text-muted-foreground">Signed in as </span><span className="font-semibold">{user?.email}</span><span className="text-muted-foreground"> · role {user?.role}</span></div>
        <div className="text-xs text-muted-foreground mt-1">Single-user platform. No public registration.</div>
      </SectionCard>
    </div>
  );
}
